import { useState, useEffect, useCallback } from 'react';
import { useZebraContext } from '../context/ZebraContext';

interface ZebraPrinter {
    name: string;
    deviceType: string;
    connection: string;
    uid: string;
    provider: string;
    manufacturer: string;
    version: number;
    [key: string]: any;
}

export const useZebraPrinter = () => {
    const [selectedPrinter, setSelectedPrinter] = useState<ZebraPrinter | null>(null);
    const [availablePrinters, setAvailablePrinters] = useState<ZebraPrinter[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isSdkReady, setIsSdkReady] = useState(false);

    // Consume Global Zebra Context
    const zebraContext = useZebraContext();
    const {
        connectHardware,
        printZPLHardware,
        isHardwareConnected,
        hardwareLoading,
        hardwareError,
        hardwareDeviceName
    } = zebraContext || {};

    const API_URL = 'https://localhost:9101';

    // Store the raw default data to use for retries
    const [rawDefaultDevice, setRawDefaultDevice] = useState<any>(null);

    useEffect(() => {
        const initService = async () => {
            // CRITICAL: If any hardware was previously paired, DON'T even try the localhost fetch
            if (isHardwareConnected || localStorage.getItem('zebra_usb_paired') === 'true' || localStorage.getItem('zebra_serial_paired') === 'true') {
                setIsSdkReady(true);
                return;
            }

            try {
                // If the user has EVER tried to use USB (even if it failed setup), 
                // we should be very careful about making noise in the console.
                if (localStorage.getItem('zebra_usb_paired') === 'true' || localStorage.getItem('zebra_serial_paired') === 'true') {
                    setIsSdkReady(true);
                    return;
                }

                const checkRes = await fetch(`${API_URL}/default?type=printer`, {
                    method: 'GET',
                    signal: AbortSignal.timeout(1500)
                });

                if (checkRes.ok) {
                    setIsSdkReady(true);
                    const defData = await checkRes.json();
                    setRawDefaultDevice(defData);

                    if (defData && defData.name) {
                        const connectedDevice = {
                            ...defData,
                            connection: API_URL + '/',
                            uid: defData.uid || defData.name,
                            provider: defData.provider || '',
                            manufacturer: defData.manufacturer || '',
                            version: defData.version || 2
                        };
                        setSelectedPrinter(connectedDevice);
                        setAvailablePrinters([connectedDevice]);
                    }
                }
            } catch (err) {
                // Ignore fetch errors to keep console clean
            }
        };

        initService();
        const interval = setInterval(() => {
            // Only retry if neither Hardware nor Browser Print are ready
            if (!isSdkReady && !isHardwareConnected && localStorage.getItem('zebra_usb_paired') !== 'true' && localStorage.getItem('zebra_serial_paired') !== 'true') {
                initService();
            }
        }, 10000); // Relaxed interval to 10s
        return () => clearInterval(interval);
    }, [isSdkReady, isHardwareConnected]);

    const getAvailablePrinters = useCallback(async () => {
        // Compatibility stub
    }, []);

    const sendPrintPayload = async (device: any, zpl: string) => {
        const payload = {
            device: device,
            data: zpl
        };

        const response = await fetch(`${API_URL}/write`, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify(payload)
        });

        if (!response.ok) {
            const txt = await response.text();
            throw new Error(txt || response.statusText);
        }
        return true;
    };

    const printLabel = async (zpl: string) => {
        setError(null);

        // Priority 1: Direct Hardware (WebUSB or WebSerial)
        if (isHardwareConnected && printZPLHardware) {
            setLoading(true);
            try {
                const success = await printZPLHardware(zpl);
                if (success) return true;
            } catch (err: any) {
                console.warn('Hardware print failed, trying fallback if available:', err);
            } finally {
                setLoading(false);
            }
        }

        // Priority 2: Zebra Browser Print (Fallback)
        if (!selectedPrinter) {
            const paired = localStorage.getItem('zebra_usb_paired') === 'true' || localStorage.getItem('zebra_serial_paired') === 'true';
            const msg = paired
                ? 'Conexión local no lista. Asegúrate de que la impresora esté encendida.'
                : 'Impresora no detectada. Conecta el USB o verifica con Configurar Hardware.';
            setError(msg);
            return false;
        }

        setLoading(true);
        try {
            if (rawDefaultDevice) {
                await sendPrintPayload(rawDefaultDevice, zpl);
                return true;
            } else {
                await sendPrintPayload(selectedPrinter, zpl);
                return true;
            }
        } catch (err: any) {
            console.error('All print strategies failed:', err);
            const msg = String(err);
            if (msg.includes('Unauthorized')) {
                setError(`Acceso bloqueado por Zebra. Verifica que "${window.location.origin}" esté en la lista de "Accepted Hosts" en la app Zebra.`);
            } else {
                setError('Error al imprimir: ' + msg);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    return {
        selectedPrinter: isHardwareConnected ? { name: hardwareDeviceName || 'Zebra Hardware' } : selectedPrinter,
        setSelectedPrinter,
        availablePrinters,
        getAvailablePrinters,
        printLabel,
        loading: loading || hardwareLoading,
        error: error || hardwareError,
        isSdkReady,
        // Expose hardware specific methods for UI
        isUSBConnected: isHardwareConnected,
        connectUSB: connectHardware,
        usbDeviceName: hardwareDeviceName
    };
};

